import api from "../api/axios";

// CDT code suggestion within a step
export interface CodeSuggestion {
  id: number;
  cdtCodeId: number;
  code: string;
  description: string;
  defaultFee: number;
}

// Treatment step (procedure) within an appointment
export interface TreatmentPlanStep {
  id: number;
  name: string; // Step name
  description?: string; // Step description
  sortOrder: number;
  durationMinutes?: number;
  fee?: number;
  codeSuggestions: CodeSuggestion[];
}

// Appointment containing multiple steps/procedures
export interface TreatmentPlanAppointment {
  id: number;
  title: string;
  description?: string | null;
  timeframeLabel: string;
  conditionalPath: string;
  notes: string;
  sortOrder: number;
  totalDurationMinutes?: number;
  totalFee?: number;
  steps: TreatmentPlanStep[];
}

export interface TreatmentPlanResponse {
  id: number;
  name: string;
  patientId?: number;
  patientUid: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  providerId: number;
  providerFirstName: string | null;
  providerLastName: string | null;
  providerAvatarUrl: string | null;
  sessionId: number;
  treatmentSummary: string;
  status: number;
  dateGenerated: string;
  approvedByProviderId: number | null;
  approvedByProviderAt: string | null;
  patientSignature: string | null;
  patientSignatureMethod: number | null;
  approvedByPatientAt: string | null;
  rejectionComment: string | null;
  declinedByPatientAt: string | null;
  lastEditedAt: string;
  lastEditedBy: number | null;
  fee: string | null;
  providerNote?: string;
  patientSummary?: string | null;
  totalDurationMinutes?: number;
  totalFee?: number;
  appointments: TreatmentPlanAppointment[];
}

export interface PublicTreatmentPlanResponse {
  id: number;
  name: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  providerFirstName: string | null;
  providerLastName: string | null;
  treatmentSummary: string;
  providerNote?: string;
  status: number;
  dateGenerated: string;
  totalFee: number;
  appointments: TreatmentPlanAppointment[];
  approvedByPatientAt?: string | null;
}

// Step data for update request
export interface StepUpdateData {
  id?: number;
  name: string;
  description?: string;
  sortOrder: number;
  durationMinutes?: number;
  fee?: number;
  customProcedureId?: number;
  isFeeEdited?: boolean;
  codeSuggestions?: {
    id?: number;
    cdtCodeId: number;
    confidence?: number;
    sortOrder?: number;
  }[];
}

// Appointment data for update request
export interface AppointmentUpdateData {
  id?: number;
  title?: string;
  timeframeLabel: string;
  conditionalPath?: string;
  description?: string;
  sortOrder: number;
  steps: StepUpdateData[];
}

// Request body for updating treatment plan
export interface TreatmentPlanUpdateRequest {
  id?: number;
  name: string;
  treatmentSummary: string;
  providerNote?: string;
  fee?: string;
  patientSummary?: string;
  status: number;
  appointments: AppointmentUpdateData[];
  hasAppointmentUpdates?: boolean;
}

export interface QRCodeResponse {
  qrCodeImage: string;
  secureUrl: string;
  expiresAt: string;
}

export interface TreatmentPlanListItem {
  id: number;
  name: string;
  patientUid: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  openDentalTreatmentPlanUid: string | null;
  providerId: number;
  providerFirstName: string | null;
  providerLastName: string | null;
  providerAvatarUrl: string | null;
  sessionId: number;
  treatmentSummary: string;
  status: number;
  dateGenerated: string;
  totalFee: number;
}

export interface GetTreatmentPlansParams {
  sessionId?: number;
  patientId?: number[];
  providerId?: number[];
  status?: number[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  start?: number;
  limit?: number;
  total?: boolean;
  orderBy?: string;
  order?: 0 | 1;
}

export interface GetTreatmentPlansResponse {
  data: TreatmentPlanListItem[];
  total?: number;
}

export interface GenerateTreatmentPlanResponse {
  treatmentPlanId: number;
  correlationId: string;
}

export const treatmentPlanService = {
  /**
   * Generate treatment plan from a session
   * Returns 202 Accepted with treatment plan ID when generation is triggered
   */
  async generate(sessionId: number): Promise<GenerateTreatmentPlanResponse> {
    const response = await api.post<GenerateTreatmentPlanResponse>(
      `/sessions/${sessionId}/treatment-plan-generation`,
    );
    return response.data;
  },

  /**
   * Get list of treatment plans with filters
   */
  async getList(
    params?: GetTreatmentPlansParams,
  ): Promise<GetTreatmentPlansResponse> {
    const queryParams = new URLSearchParams();

    if (params?.sessionId !== undefined) {
      queryParams.append("sessionId", params.sessionId.toString());
    }
    if (params?.patientId?.length) {
      params.patientId.forEach((id) => {
        queryParams.append("patientId", id.toString());
      });
    }
    if (params?.providerId?.length) {
      params.providerId.forEach((id) => {
        queryParams.append("providerId", id.toString());
      });
    }
    if (params?.status?.length) {
      params.status.forEach((s) => {
        queryParams.append("status", s.toString());
      });
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.dateFrom) {
      queryParams.append("dateFrom", params.dateFrom);
    }
    if (params?.dateTo) {
      queryParams.append("dateTo", params.dateTo);
    }
    if (params?.start !== undefined) {
      queryParams.append("start", params.start.toString());
    }
    if (params?.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params?.total !== undefined) {
      queryParams.append("total", params.total.toString());
    }
    if (params?.orderBy) {
      queryParams.append("order-by", params.orderBy);
    }
    if (params?.order !== undefined) {
      queryParams.append("order", params.order.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/treatment-plans?${queryString}`
      : "/treatment-plans";
    const response = await api.get<TreatmentPlanListItem[]>(url);
    return { data: response.data };
  },

  /**
   * Get treatment plan by ID
   */
  async getById(id: number | string): Promise<TreatmentPlanResponse> {
    const response = await api.get<TreatmentPlanResponse>(
      `/treatment-plans/${id}`,
    );
    return response.data;
  },

  /**
   * Update treatment plan (including adding/editing steps)
   */
  async update(
    id: number | string,
    data: TreatmentPlanUpdateRequest,
  ): Promise<TreatmentPlanResponse> {
    const response = await api.put<TreatmentPlanResponse>(
      `/treatment-plans/${id}`,
      data,
    );
    return response.data;
  },

  /**
   * Approve treatment plan by provider
   */
  async approve(
    id: number | string,
    providerId: number,
  ): Promise<TreatmentPlanResponse> {
    const response = await api.post<TreatmentPlanResponse>(
      `/treatment-plans/${id}/approve`,
      { providerId },
    );
    return response.data;
  },

  /**
   * Unapprove treatment plan (revert to awaiting review)
   */
  async unapprove(id: number | string): Promise<TreatmentPlanResponse> {
    const response = await api.post<TreatmentPlanResponse>(
      `/treatment-plans/${id}/unapprove`,
    );
    return response.data;
  },

  /**
   * Generate QR code for patient to sign treatment plan
   */
  async generateQR(id: number | string): Promise<QRCodeResponse> {
    const response = await api.post<QRCodeResponse>(
      `/treatment-plans/${id}/share/generate-qr`,
    );
    return response.data;
  },

  /**
   * Send treatment plan to patient via email for review
   */
  async shareByEmail(id: number | string): Promise<void> {
    await api.post(`/treatment-plans/${id}/share/email`);
  },

  /**
   * Send OTP code for treatment plan access
   */
  async sendOtp(token: string): Promise<void> {
    await api.post("/public/treatment-plans/send-otp", { token });
  },

  /**
   * Get treatment plan by token with optional OTP verification (public endpoint for patient review)
   */
  async getByToken(
    token: string,
    otpCode?: string,
    signal?: AbortSignal,
  ): Promise<PublicTreatmentPlanResponse> {
    const response = await api.post<PublicTreatmentPlanResponse>(
      `/public/treatment-plans/${token}/review`,
      otpCode ? { otpCode } : {},
      { signal },
    );
    return response.data;
  },

  /**
   * Approve treatment plan by patient with signed PDF
   * @param token - Secure access token from QR code or email link
   * @param pdfFileBase64 - Signed PDF file as base64 string
   * @param pdfFileName - Name of the PDF file
   */
  async approveByPatient(
    token: string,
    pdfFileBase64: string,
    pdfFileName: string,
  ): Promise<PublicTreatmentPlanResponse> {
    const response = await api.post<PublicTreatmentPlanResponse>(
      `/public/treatment-plans/${token}/approve`,
      { pdfFileBase64, pdfFileName },
    );
    return response.data;
  },

  /**
   * Reject/decline treatment plan by patient
   * @param token - Secure access token from QR code or email link
   * @param comment - Optional rejection comment
   */
  async rejectByPatient(
    token: string,
    comment?: string,
  ): Promise<PublicTreatmentPlanResponse> {
    const response = await api.post<PublicTreatmentPlanResponse>(
      `/public/treatment-plans/${token}/reject`,
      { comment: comment || "" },
    );
    return response.data;
  },
};
